import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { platformStaffAPI } from '../../../services';
import { UsersRound, ArrowLeft } from 'lucide-react';

const MODULES = [
  { key: 'clients', label: 'Clients' },
  { key: 'separations', label: 'Separations' },
  { key: 'expenditures', label: 'Expenditures' },
  { key: 'profits', label: 'Profits' },
  { key: 'analytics', label: 'Analytics' },
  { key: 'contactForms', label: 'Contact Forms' },
  { key: 'platformSupport', label: 'Platform Support' },
  { key: 'settings', label: 'Settings' },
  { key: 'quotations', label: 'Quotations' },
  { key: 'billing', label: 'Billing' },
  { key: 'announcements', label: 'Announcements' },
  // The audit trail is append-only - only 'read' has any effect here
  { key: 'audit', label: 'Audit Log' }
];
const ACTIONS = ['create', 'read', 'update', 'delete'];

const emptyPermissions = () =>
  Object.fromEntries(MODULES.map((m) => [m.key, { create: false, read: false, update: false, delete: false }]));

const StaffFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const [form, setForm] = useState({
    name: '',
    username: '',
    email: '',
    password: '',
    active: true,
    platformPermissions: emptyPermissions()
  });
  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isEditMode) return;
    (async () => {
      try {
        const res = await platformStaffAPI.getStaffMember(id);
        const s = res.data;
        setForm({
          name: s.name || '',
          username: s.username || '',
          email: s.email || '',
          password: '',
          active: s.active !== false,
          platformPermissions: { ...emptyPermissions(), ...(s.platformPermissions || {}) }
        });
      } catch {
        setError('Failed to load staff member');
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isEditMode]);

  const togglePermission = (moduleKey, action) => {
    setForm((f) => ({
      ...f,
      platformPermissions: {
        ...f.platformPermissions,
        [moduleKey]: { ...f.platformPermissions[moduleKey], [action]: !f.platformPermissions[moduleKey][action] }
      }
    }));
  };

  const toggleModuleRow = (moduleKey, checked) => {
    setForm((f) => ({
      ...f,
      platformPermissions: {
        ...f.platformPermissions,
        [moduleKey]: { create: checked, read: checked, update: checked, delete: checked }
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.name.trim() || !form.username.trim() || !form.email.trim() || (!isEditMode && !form.password.trim())) {
      setError('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        username: form.username.trim(),
        email: form.email.trim(),
        active: form.active,
        platformPermissions: form.platformPermissions
      };
      if (form.password.trim()) payload.password = form.password.trim();

      if (isEditMode) {
        await platformStaffAPI.updateStaffMember(id, payload);
      } else {
        await platformStaffAPI.createStaffMember(payload);
      }
      navigate('/dashboard/staff');
    } catch (err) {
      setError(err?.message || 'Failed to save staff member');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-12 text-center text-gray-400">Loading...</div>;

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <button onClick={() => navigate('/dashboard/staff')} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4">
        <ArrowLeft className="w-4 h-4" /> Back to Staff Management
      </button>

      <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2 mb-6">
        <UsersRound className="w-6 h-6 text-blue-600" /> {isEditMode ? 'Edit Staff Member' : 'Add Staff Member'}
      </h1>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white rounded-xl shadow-sm border p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Username <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={form.username}
              onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
              disabled={isEditMode}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email <span className="text-red-500">*</span></label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Password {isEditMode ? <span className="text-gray-400">(leave blank to keep unchanged)</span> : <span className="text-red-500">*</span>}
            </label>
            <input
              type="password"
              value={form.password}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            />
          </div>
          {isEditMode && (
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} />
              Active
            </label>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h2 className="text-sm font-semibold text-gray-900 mb-4">Module Permissions</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 pr-4 font-medium text-gray-500">Module</th>
                  {ACTIONS.map((a) => (
                    <th key={a} className="text-center py-2 px-3 font-medium text-gray-500 capitalize">{a}</th>
                  ))}
                  <th className="text-center py-2 px-3 font-medium text-gray-500">All</th>
                </tr>
              </thead>
              <tbody>
                {MODULES.map((m) => {
                  const perms = form.platformPermissions[m.key];
                  const allChecked = ACTIONS.every((a) => perms[a]);
                  return (
                    <tr key={m.key} className="border-b border-gray-100">
                      <td className="py-2 pr-4 text-gray-900">{m.label}</td>
                      {ACTIONS.map((a) => (
                        <td key={a} className="text-center py-2 px-3">
                          <input type="checkbox" checked={perms[a]} onChange={() => togglePermission(m.key, a)} />
                        </td>
                      ))}
                      <td className="text-center py-2 px-3">
                        <input type="checkbox" checked={allChecked} onChange={(e) => toggleModuleRow(m.key, e.target.checked)} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex gap-3">
          <button type="button" onClick={() => navigate('/dashboard/staff')} className="flex-1 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">
            Cancel
          </button>
          <button type="submit" disabled={saving} className="flex-1 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-60">
            {saving ? 'Saving...' : isEditMode ? 'Save Changes' : 'Create Staff Member'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default StaffFormPage;
